import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Spells = ({ user }: any) => {
  const location = useLocation();
  const [entries, setEntries] = useState([]);
  const [name, setName] = useState('');
  const [intent, setIntent] = useState('');
  const [ingredients, setIngredients] = useState('');
  const [timing, setTiming] = useState('');
  const [lunarPhase, setLunarPhase] = useState('');
  const [results, setResults] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [includeLunar, setIncludeLunar] = useState(true);
  const [editingResultsId, setEditingResultsId] = useState<number | null>(null);
  const [resultsBuffer, setResultsBuffer] = useState('');

  const fetchSpells = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/spells`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setEntries(res.data);
    } catch (err) {
      console.error('Failed to fetch spells:', err);
    }
  }, [user.token]);

  useEffect(() => {
    // Check location state
    if (location.state && (location.state as any).prefillIngredient) {
      const item = (location.state as any).prefillIngredient;
      setIngredients(prev => prev ? `${prev}, ${item}` : item);
      window.history.replaceState({}, document.title);
    } 
    // Check query params
    const queryParams = new URLSearchParams(location.search);
    const prefill = queryParams.get('prefill');
    if (prefill) {
      setIngredients(prev => prev ? `${prev}, ${prefill}` : prefill);
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location]);

  useEffect(() => {
    const fetchCurrentAstro = async () => {
      try {
        const lunarRes = await axios.get(`${API_URL}/lunar-cycle`);
        
        if (Array.isArray(lunarRes.data)) {
            const dayLunar = lunarRes.data.find((d: any) => d.date === date);
            if (dayLunar) {
                setLunarPhase(dayLunar.phaseName);
            }
        }
      } catch (err) {
        console.error('Failed to fetch astro data for date', err);
      }
    };
    fetchCurrentAstro();
  }, [date]);

  useEffect(() => {
    fetchSpells();
  }, [fetchSpells]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { 
        name, intent, ingredients, timing, results, 
        is_private: isPrivate ? 1 : 0,
        lunar_phase: includeLunar ? lunarPhase : null,
        date
      };

      if (editingId) {
        await axios.put(`${API_URL}/spells/${editingId}`, payload, {
            headers: { Authorization: `Bearer ${user.token}` }
        });
        setEditingId(null);
      } else {
        await axios.post(`${API_URL}/spells`, payload, {
            headers: { Authorization: `Bearer ${user.token}` }
        });
      }

      setName(''); setIntent(''); setIngredients(''); setTiming(''); setResults('');
      fetchSpells();
    } catch (err) {
      console.error('Failed to save spell:', err);
      alert('Failed to save spell. Please check the console for details.');
    }
  };

  const startEdit = (spell: any) => {
    setEditingId(spell.id);
    setName(spell.name);
    setIntent(spell.intent || '');
    setIngredients(spell.ingredients || '');
    setTiming(spell.timing || '');
    setLunarPhase(spell.lunar_phase || '');
    setIncludeLunar(!!spell.lunar_phase);
    setResults(spell.results || '');
    setIsPrivate(spell.is_private === 1);
    setDate(spell.date || new Date().toISOString().split('T')[0]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveResults = async (id: number) => {
    try {
      await axios.put(`${API_URL}/spells/${id}`, 
        { results: resultsBuffer },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setEditingResultsId(null);
      fetchSpells();
    } catch (err) {
      console.error('Failed to update results:', err);
      alert('Failed to update results.');
    }
  };

  const togglePrivacy = async (id: number, currentPrivacy: number) => {
    await axios.put(`${API_URL}/spells/${id}/privacy`, 
      { is_private: currentPrivacy === 1 ? 0 : 1 },
      { headers: { Authorization: `Bearer ${user.token}` } }
    );
    fetchSpells();
  };

  const deleteSpell = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this spell log?')) {
      try {
        await axios.delete(`${API_URL}/spells/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchSpells();
      } catch (err) {
        console.error('Failed to delete spell', err);
        alert('Failed to delete spell.');
      }
    }
  };

  return (
    <div className="content">
      <h2>Spell Tracker</h2>
      <div className="card">
        <h3>{editingId ? 'Edit Spell' : 'Log a New Spell'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0' }}>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} required />
            <input placeholder="Spell Name" value={name} onChange={e => setName(e.target.value)} required />
          </div>
          <input placeholder="Intent / Purpose" value={intent} onChange={e => setIntent(e.target.value)} />
          <textarea placeholder="Ingredients (e.g. herbs, candles, stones)" value={ingredients} onChange={e => setIngredients(e.target.value)} rows={3} />
          
          <div className="grid" style={{ gridTemplateColumns: '1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input type="checkbox" checked={includeLunar} onChange={e => setIncludeLunar(e.target.checked)} style={{ width: 'auto', marginBottom: 0 }} />
                Include Lunar Phase
              </label>
              <select value={lunarPhase} onChange={e => setLunarPhase(e.target.value)} disabled={!includeLunar}>
                <option value="">Select Lunar Phase</option>
                <option value="New Moon">New Moon</option>
                <option value="Waxing Crescent">Waxing Crescent</option>
                <option value="First Quarter">First Quarter</option>
                <option value="Waxing Gibbous">Waxing Gibbous</option>
                <option value="Full Moon">Full Moon</option>
                <option value="Waning Gibbous">Waning Gibbous</option>
                <option value="Last Quarter">Last Quarter</option>
                <option value="Waning Crescent">Waning Crescent</option>
              </select>
            </div>
          </div>

          <input placeholder="Specific Timing (e.g. Friday at 3AM)" value={timing} onChange={e => setTiming(e.target.value)} />
          <textarea placeholder="Initial Results / Notes" value={results} onChange={e => setResults(e.target.value)} rows={3} />
          
          <div style={{ marginBottom: '1rem' }}>
            <label>
              <input type="checkbox" checked={isPrivate} onChange={e => setIsPrivate(e.target.checked)} style={{ width: 'auto', marginRight: '0.5rem' }} />
              Keep Private
            </label>
          </div>
          <button type="submit">{editingId ? 'Update Ritual' : 'Cast & Record'}</button>
          {editingId && (
            <button 
              type="button" 
              onClick={() => { setEditingId(null); setName(''); setIntent(''); setIngredients(''); setTiming(''); setResults(''); }}
              style={{ marginLeft: '1rem', backgroundColor: '#666' }}
            >
              Cancel
            </button>
          )}
        </form>
      </div>

      <div className="grid">
        {entries.map((spell: any) => (
          <div key={spell.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3>{spell.name}</h3>
                <small style={{ color: 'var(--gold)' }}>{spell.date}</small>
              </div>
              <span className="badge" style={{ backgroundColor: spell.is_private ? 'var(--accent-purple)' : '#2e7d32' }}>
                {spell.is_private ? 'Private' : 'Public'}
              </span>
            </div>
            <p><strong>Intent:</strong> {spell.intent}</p>
            <div style={{ marginBottom: '0.5rem' }}>
              {spell.lunar_phase && <span className="badge">🌙 {spell.lunar_phase}</span>}
            </div>
            <p><strong>Ingredients:</strong> {spell.ingredients}</p>
            <p><strong>Timing:</strong> {spell.timing}</p>
            <div style={{ borderTop: '1px solid var(--accent-purple)', paddingTop: '1rem', marginTop: '1rem' }}>
              <strong>Results & Manifestations:</strong>
              {editingResultsId === spell.id ? (
                <div style={{ marginTop: '0.5rem' }}>
                  <textarea 
                    value={resultsBuffer} 
                    onChange={e => setResultsBuffer(e.target.value)}
                    rows={4}
                    placeholder="Describe how the magic manifested..."
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => saveResults(spell.id)} style={{ padding: '0.3rem 0.8rem', fontSize: '0.8rem' }}>Save Manifestation</button>
                    <button onClick={() => setEditingResultsId(null)} style={{ padding: '0.3rem 0.8rem', fontSize: '0.8rem', backgroundColor: '#666' }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <p style={{ fontStyle: 'italic', marginTop: '0.5rem' }}>{spell.results || 'Still weaving into reality...'}</p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              <button onClick={() => startEdit(spell)}>Edit Full Log</button>
              {editingResultsId !== spell.id && (
                <button onClick={() => { setEditingResultsId(spell.id); setResultsBuffer(spell.results || ''); }}>
                  Update Results
                </button>
              )}
              <button onClick={() => togglePrivacy(spell.id, spell.is_private)}>
                {spell.is_private ? 'Publish' : 'Make Private'}
              </button>
              <button onClick={() => deleteSpell(spell.id)} style={{ backgroundColor: '#8b0000' }}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Spells;